<script setup>
import IconChevronBottom from "@/assets/icons/IconChevronBottom.vue";
import {computed, ref} from "vue";

const props = defineProps({
  modelMonth: {
    type: String,
    default: "january"
  },
  modelYear: {
    type: Number,
    default: new Date().getFullYear()
  }
});

const emit = defineEmits([
  "update:modelMonth",
  "update:modelYear"
]);

// Выбор даты (месяц и год)
const isShowMonthDropdown = ref(false);
const isShowYearDropdown = ref(false);
const dateViewRef = ref(null);

const toggleShowMonthDropdown = () => {
  isShowMonthDropdown.value = !isShowMonthDropdown.value;
  if (isShowMonthDropdown.value) {
    isShowYearDropdown.value = false;
  }
};

const toggleShowYearDropdown = () => {
  isShowYearDropdown.value = !isShowYearDropdown.value;
  if (isShowYearDropdown.value) {
    isShowMonthDropdown.value = false;
  }
};

const monthList = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december"
];

const monthKeys = {
  all: "Все",
  january: "Январь",
  february: "Февраль",
  march: "Март",
  april: "Апрель",
  may: "Май",
  june: "Июнь",
  july: "Июль",
  august: "Август",
  september: "Сентябрь",
  october: "Октябрь",
  november: "Ноябрь",
  december: "Декабрь"
};

const activeMonth = computed({
  get: () => props.modelMonth,
  set: (value) => emit("update:modelMonth", value)
});

const activeYear = computed({
  get: () => props.modelYear,
  set: (value) => emit("update:modelYear", value)
});

// Генерация списка последних 10 лет
const yearList = computed(() => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 10 }, (_, i) => currentYear - i);
});

const handleCloseMonth = (event) => {
  const clickedOnMonth = event.target.closest(".date-view__month");
  if (clickedOnMonth) {
    return;
  }
  isShowMonthDropdown.value = false;
};

const handleCloseYear = (event) => {
  const clickedOnYear = event.target.closest(".date-view__year");
  if (clickedOnYear) {
    return;
  }
  isShowYearDropdown.value = false;
};
</script>

<template>
  <div class="date-view-wrapper">
    <SimpleDropdown
      teleport="#app"
      :use-cross-icon="false"
      :use-title="false"
      max-width="130px"
      max-height="274px"
      :use-searchbar="false"
      :is-visible="isShowMonthDropdown"
      position="bottom-left"
      :offset="7"
      @close="event => handleCloseMonth(event)"
    >
      <template #trigger>
        <div
          ref="dateViewRef"
          class="date-view"
        >
          <div
            class="date-view__month"
            @click="toggleShowMonthDropdown"
          >
            {{ monthKeys[activeMonth] }}
            <span
              class="selector__icon"
              :class="{'selector__icon--active': isShowMonthDropdown}"
            >
              <IconChevronBottom />
            </span>
          </div>

          <div class="date-view__separator" />

          <div
            class="date-view__year"
            @click="toggleShowYearDropdown"
          >
            {{ activeYear }}
            <span
              class="selector__icon"
              :class="{'selector__icon--active': isShowYearDropdown}"
            >
              <IconChevronBottom />
            </span>
          </div>
        </div>
      </template>
      <template #dropdown-item>
        <ListView>
          <ListItem
            v-for="(month, index) in monthList"
            :key="index"
            v-model="activeMonth"
            :item="month"
          >
            {{ monthKeys[month] }}
          </ListItem>
        </ListView>
      </template>
    </SimpleDropdown>

    <SimpleDropdown
      teleport="#app"
      :use-cross-icon="false"
      :use-title="false"
      max-width="100px"
      max-height="274px"
      :use-searchbar="false"
      :is-visible="isShowYearDropdown"
      position="bottom-right"
      :offset="19"
      @close="event => handleCloseYear(event)"
    >
      <template #trigger>
        <div class="date-view-year-trigger" />
      </template>
      <template #dropdown-item>
        <ListView>
          <ListItem
            v-for="year in yearList"
            :key="year"
            v-model="activeYear"
            :item="year"
          >
            {{ year }}
          </ListItem>
        </ListView>
      </template>
    </SimpleDropdown>
  </div>
</template>

<style scoped lang="scss">
.date-view-wrapper {
  position: relative;
  width: 196px;
  height: 36px;
}

.date-view {
  display: grid;
  grid-template-columns: 80px 2px 55px;
  align-items: center;
  justify-content: space-between;
  background-color: var(--background-bg-white);
  padding: 6px 16px;
  gap: 8px;
  border-radius: 8px;
  height: 36px;
  width: 196px;

  &__separator {
    width: 2px;
    height: 100%;
    background-color: var(--gray-gray30);
  }

  &__icon {
    color: var(--icon-icon-secondary);
  }

  &__month {
    align-items: center;
    display: flex;
    justify-content: space-between;
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    color: var(--text-text-main);
    cursor: pointer;
  }

  &__year {
    justify-content: space-between;
    align-items: center;
    display: flex;
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    color: var(--text-text-main);
    cursor: pointer;
  }
}

.date-view-year-trigger {
  position: absolute;
  top: 0;
  left: 0;
  width: 196px;
  height: 36px;
  pointer-events: none;
}

.selector__icon {
  color: var(--icon-icon-secondary);
  margin-left: auto;
  cursor: pointer;
  user-select: none;
  transition: transform .2s;
  width: 14px;
  height: 14px;

  &--active {
    color: var(--icon-icon-on);
    transform: rotate(180deg);
  }
}
</style>